import { Affix, Button, Drawer, message, Modal, Divider } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashValues from 'lodash/values';
import React, { useEffect, useState, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import DrawerTitleBar from 'src/components/common/DrawerTitleBar';
import CustomColumnsDataCard from './customColumnDrawerParts/CustomColumnsDataCard';
import { CollapsedMetricCard } from './customColumnDrawerParts/CollapsedMetricCard';
import { DraftMetricCard } from './customColumnDrawerParts/DraftMetricCard';
import { validateMetricDeletion } from './customColumnDrawerParts/validationUtils';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { 
    METRICS_CHANGED, 
    SET_REPORT_MASTER_DATA,
    ADD_CUSTOM_METRIC,
    DELETE_CUSTOM_METRIC,
    EXPAND_CUSTOM_METRIC,
    UPDATE_DRAFT_METRIC,
    CLEAR_DRAFT_METRIC,
    START_EDITING_CUSTOM_METRIC,
    SAVE_EDITED_CUSTOM_METRIC,
    CANCEL_EDITING_CUSTOM_METRIC,
} from 'src/reduxActions/actionNameEnums';
import useLazyAPI from 'src/components/common/hooks/useLazyAPI';
import { GET_CALCULATION_METADATA } from 'src/api/routes';
import { SET_CALCULATION_METADATA } from 'src/reduxActions/actionNameEnums';
import { CalculationMetadataResponse } from './calculationMetadataTypes';
import { getDownloadRowLimitWarningMessage } from '../chartBuilderUtils';
import { useTranslation } from 'react-i18next';
import { AddedMetric, DraftMetric } from 'src/reducers/customMetrices';
import { AvailableField } from './customColumnDrawerParts/EnhancedSqlEditor';

const useStyle = createUseStyles({
    descriptionText: {
        height: '12px',
        color: '#565656',
        marginBottom: '16px',
    },
    body: {
        padding: '16px',
    },
    addMoreButton: {
        color: '#1D6EC3',
        marginTop: '10px',
        cursor: 'pointer',
    },
    affixStyle: {
        position: 'sticky',
        top: '0px',
        zIndex: '2',
    },
    addedSection: {
        background: '#f5f5f5',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '20px',
    },
    draftSection: {
        marginBottom: '20px',
    },
    sectionHeader: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#8C8C8C',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '12px',
    },
});

const newUnfilledFormData = () => {
    return {name: '', prettyName: '', type: '', sql: '', id: uuidv4() };
};

export const getOperations = (type:string) => {
    if(type === 'number'){
        return [
            { value: 'avg', prettyName: 'Average', type: 'number'},
            { value: 'count', prettyName: 'Count', type: 'number'},
            { value: 'count_distinct', prettyName: 'Count Distinct', type: 'number'},
            { value: 'max', prettyName: 'Maximum', type: 'number'},
            { value: 'min', prettyName: 'Minimum', type: 'number'},
            { value: 'sum', prettyName: 'Sum', type: 'number'},
            { value: 'any', prettyName: 'Any', type: 'number'}];
    }
    if(type === 'boolean'){
        return [
            { value: 'count', prettyName: 'Count', type: 'number'},
            { value: 'count_true', prettyName: 'Count True', type: 'number'},
            { value: 'count_not_true', prettyName: 'Count Not True', type: 'number'},
            { value: 'any', prettyName: 'Any', type: 'boolean'}];
    }
    if(type === 'text'){
        return [
            { value: 'count', prettyName: 'Count', type: 'number'},
            { value: 'count_distinct', prettyName: 'Count Distinct', type: 'number'},
            { value: 'any', prettyName: 'Any', type: 'text'}];
    }
    if(type === 'timestampz'){
        return [
            { value: 'count', prettyName: 'Count', type: 'number'},
            { value: 'count_distinct', prettyName: 'Count Distinct', type: 'number'},
            { value: 'max', prettyName: 'Maximum', type: 'timestampz'},
            { value: 'min', prettyName: 'Minimum', type: 'timestampz'},
            { value: 'any', prettyName: 'Any', type: 'timestampz'}];
    }
    if(type === 'date'){
        return [
            { value: 'count', prettyName: 'Count', type: 'number'},
            { value: 'count_distinct', prettyName: 'Count Distinct', type: 'number'},
            { value: 'max', prettyName: 'Maximum', type: 'date'},
            { value: 'min', prettyName: 'Minimum', type: 'date'},
            { value: 'any', prettyName: 'Any', type: 'date'}];
    }
    return [];
};

export const buildObjectFromCustomColumnObj = (obj:any, customColumnType) => {
    if(customColumnType === 'global'){
        return {
            ...obj,
            value: obj.name,
            groupBy: {
                allowed: true,
                buckets: (obj.type === 'timestampz' || obj.type === 'date') ? [
                    { prettyName: 'Hour', value: 'hour'},
                    { prettyName: 'Day', value: 'day'},
                    { prettyName: 'Week', value: 'week'},
                    { prettyName: 'Month', value: 'month'},
                    { prettyName: 'Quarter', value: 'quarter'},
                    { prettyName: 'Year', value: 'year'}]:[],
            },
            operations: getOperations(obj.type),
            filterMetaData: {
                required: false,
            },
            customColumnType,
        };
    }
    if(customColumnType === 'local'){
        return {
            ...obj,
            value: obj.name,
            groupBy: {
                allowed: false,
            },
            operations: null,
            filterMetaData: {
                required: false,
            },
            customColumnType,
        };
    }
    return obj;
};

const checkIfAnyValueIsEmpty = (obj) => {
    const keys = Object.keys(obj);
    return keys.some((key) => {
        return obj[key] === '';
    });
};

const CustomColumnDrawer = (props) => {
    const { t } = useTranslation();
    const { columns=[], onChange, onRemove, visible, customColumnType, setCustomColumnType, setReset,
        hideAddMoreOption, useEnhancedCustomMetrics = true } = props;
    const state = useSelector((state:any) => state);
    const selectedReport = state.reportMetaData.selectedReport;
    const customMetricesState = state.customMetrices[customColumnType];
    const customMetrices = useEnhancedCustomMetrics 
        ? (customMetricesState?.added || {})
        : (customMetricesState?.added ? customMetricesState.added : customMetricesState || {});
    const dispatch = useDispatch();
    const classes = useStyle();
    const metricData = useSelector((state:any) => state.metrics) || {};
    const summarizationData = useSelector((state: any) => state.summarization) || {};
    const downloadRowLimitConfig = useSelector((state:any) => state?.masterData?.organisationConfig?.downloadRowLimitConfig);
    const enableRedshiftDump = useSelector((state:any) => state?.masterData?.organisationConfig?.enableRedshiftDump);
    const selectedReportType = useSelector((state: any) => state.reportMetaData.selectedReport);
    const selectedDbType = useSelector((state: any) => state.reportConfig?.reports[selectedReportType]?.dbType);
    const maxElasticSearchRowsToDownload = useSelector((state: any) => state.masterData.organisationConfig?.maxElasticSearchRowsToDownload);
    
    const addedMetrics: AddedMetric[] = customMetricesState?.added ? Object.values(customMetricesState.added) : [];
    const draftMetric: DraftMetric | null = customMetricesState?.draft || null;
    
    const calculationFunctions = useSelector((state: any) => state.calculationMetadata?.functions) || [];
    const cachedReportType = useSelector(
        (state: any) => state.calculationMetadata?.lastFetched?.reportType,
    );
    const lastFetchedRef = useRef<string | null>(null);
    
    const backendMetrics = state.reportConfig?.reports?.[selectedReport]?.metrics || [];
    
    const [fetchCalculationMetadata] = 
        useLazyAPI<CalculationMetadataResponse>(GET_CALCULATION_METADATA, {
            type: 'GET',
            onCompleted: (data) => {
                dispatch({
                    type: SET_CALCULATION_METADATA,
                    payload: {
                        functions: data.functions || [],
                        lastFetched: { reportType: selectedReport },
                    },
                });
                lastFetchedRef.current = selectedReport;
            },
            onError: (error) => {
                console.error('Failed to fetch calculation metadata:', error);
                if (lastFetchedRef.current === selectedReport) {
                    lastFetchedRef.current = null;
                }
            },
        });
    
    const getAvailableFieldsForMetric = (metricId?: string): AvailableField[] => {
        let metricsToInclude = addedMetrics;
        
        if (metricId) {
            const metricIndex = addedMetrics.findIndex((m) => m.id === metricId);
            if (metricIndex >= 0) {
                metricsToInclude = addedMetrics.slice(0, metricIndex);
            }
        }
        
        return [
            ...backendMetrics.map((m: any) => ({
                prettyName: m.prettyName || m.alias || m.value,
                type: m.type || 'text',
                source: 'backend' as const,
            })),
            ...metricsToInclude.map((m) => ({
                prettyName: m.prettyName,
                type: m.type,
                source: 'custom' as const,
            })),
        ];
    };

    const availableFields: AvailableField[] = getAvailableFieldsForMetric();
    
    const existingMetricNames = addedMetrics.map((m) => m.prettyName);

    useEffect(() => {
        if (visible && selectedReport && useEnhancedCustomMetrics) {
            const hasCachedData = cachedReportType === selectedReport || lastFetchedRef.current === selectedReport;
            if (!hasCachedData) {
                lastFetchedRef.current = selectedReport;
                fetchCalculationMetadata({ variables: { reportType: selectedReport } });
            }
        }
    }, [visible, selectedReport, useEnhancedCustomMetrics]);
    
    useEffect(() => {
        if (useEnhancedCustomMetrics && customColumnType && !draftMetric && addedMetrics.length === 0 && visible) {
            dispatch({
                type: UPDATE_DRAFT_METRIC,
                payload: {
                    customColumnType,
                    draft: {
                        id: uuidv4(),
                        prettyName: '',
                        type: '',
                        sql: '',
                    },
                },
            });
        }
    }, [customColumnType, draftMetric, addedMetrics.length, visible, useEnhancedCustomMetrics]);

    const addMoreColumn = () => {
        const newUnfilledForm = newUnfilledFormData();
        onChange(newUnfilledForm.id, newUnfilledForm);
    };
    const [toRemove,setToRemove] = useState<boolean>(false);
    useEffect(() => {
        if(!useEnhancedCustomMetrics && customColumnType !== '' && lodashIsEmpty(customMetrices) === true){
            addMoreColumn();
        }
    },[customColumnType, useEnhancedCustomMetrics]);

    const addCustomFieldsToMasterData = (metricsToAdd?: any) => {
        try{
            const metricsObj = metricsToAdd || customMetrices;
            const keys = Object.keys(metricsObj);
            if(keys.some((key) => checkIfAnyValueIsEmpty(metricsObj[key]))){
                throw new Error(t('Any Field should not be empty'));
            }
            const buildObjArray = keys.map((key) => {
                return buildObjectFromCustomColumnObj(metricsObj[key],customColumnType);
            });

            if(customColumnType === 'global'){
                let metrices = state.reportConfig.reports[selectedReport].metrics;
                metrices = metrices.filter(item => !item.customColumnType || !(item.customColumnType === customColumnType));
                metrices = [...metrices, ...buildObjArray];
                const payload = {
                    ...state.masterData,
                    reports:{
                        ...state.reportConfig.reports,
                        [selectedReport]: {
                            ...state.reportConfig.reports[selectedReport],
                            metrics: metrices,
                        },
                    },
                };
                dispatch({type: SET_REPORT_MASTER_DATA, payload});
            } else if (customColumnType === 'local') {
                const previousColumnsLength = Object.keys({ ...metricData, ...summarizationData }).length;
                const metric = lodashValues(metricData);
                const toRemoveMetric = metric.filter(item => item.customColumnType === 'local');

                toRemoveMetric.forEach(item => {
                    delete metricData[item.value];
                });
                buildObjArray.forEach(item => {
                    metricData[item.value] = item;
                });

                const currentColumnsLength = Object.keys({ ...metricData, ...summarizationData }).length;
                const downloadRowLimitWarningMessage: string | undefined = getDownloadRowLimitWarningMessage({
                    downloadRowLimitConfig,
                    previousColumnsLength,
                    currentColumnsLength,
                    enableRedshiftDump,
                    selectedDbType,
                    maxElasticSearchRowsToDownload,
                });

                if (downloadRowLimitWarningMessage) {
                    message.warning(downloadRowLimitWarningMessage);
                }

                dispatch({
                    type: METRICS_CHANGED,
                    payload: metricData,
                });
            }
        }
        catch(err){
            message.error((err as Error).message);
        }
    };

    const addCustomFields = () => {
        if (useEnhancedCustomMetrics) {
            if (addedMetrics.length > 0) {
                const metricsObj = addedMetrics.reduce((acc, m) => {
                    acc[m.id] = m;
                    return acc;
                }, {} as Record<string, AddedMetric>);
                addCustomFieldsToMasterData(metricsObj);
            }
        } else {
            addCustomFieldsToMasterData();
        }
        setCustomColumnType('');
    };

    const handleAddMetric = (metric: AddedMetric) => {
        dispatch({
            type: ADD_CUSTOM_METRIC,
            payload: { customColumnType, metric },
        });
    };

    const handleDeleteMetric = (metricId: string) => {
        const metric = addedMetrics.find((m) => m.id === metricId);
        if (!metric) return;

        const { canDelete, dependents } = validateMetricDeletion(metric, addedMetrics);

        if (!canDelete) {
            Modal.warning({
                title: t('Cannot Delete Metric'),
                icon: <ExclamationCircleOutlined />,
                content: (
                    <div>
                        <p>{t('This metric is referenced by other metrics')}:</p>
                        <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                            {dependents.map((dep) => (
                                <li key={dep.id}>
                                    <strong>{dep.prettyName}</strong>
                                </li>
                            ))}
                        </ul>
                        <p>{t('Please remove references first, or delete dependent metrics.')}</p>
                    </div>
                ),
                okText: t('Understood'),
            });
            return;
        }

        dispatch({
            type: DELETE_CUSTOM_METRIC,
            payload: { customColumnType, metricId },
        });
    };

    const handleExpandMetric = (metricId: string) => {
        dispatch({
            type: EXPAND_CUSTOM_METRIC,
            payload: { customColumnType, metricId },
        });
    };

    const handleEditMetric = (metricId: string) => {
        dispatch({
            type: START_EDITING_CUSTOM_METRIC,
            payload: { customColumnType, metricId },
        });
    };

    const handleSaveEdit = (metricId: string, updates: Partial<AddedMetric>) => {
        dispatch({
            type: SAVE_EDITED_CUSTOM_METRIC,
            payload: { customColumnType, metricId, updates },
        });
    };

    const handleCancelEdit = (metricId: string) => {
        dispatch({
            type: CANCEL_EDITING_CUSTOM_METRIC,
            payload: { customColumnType, metricId },
        });
    };

    const handleUpdateDraft = (draft: DraftMetric) => {
        dispatch({
            type: UPDATE_DRAFT_METRIC,
            payload: { customColumnType, draft },
        });
    };

    const handleCancelDraft = () => {
        dispatch({
            type: CLEAR_DRAFT_METRIC,
            payload: { customColumnType },
        });
    };

    const handleAddAnotherMetric = () => {
        dispatch({
            type: UPDATE_DRAFT_METRIC,
            payload: {
                customColumnType,
                draft: {
                    id: uuidv4(),
                    prettyName: '',
                    type: '',
                    sql: '',
                },
            },
        });
    };

    useEffect(() => {
        if(toRemove){
            addCustomFieldsToMasterData();
            setToRemove(false);
        }
    },[toRemove]);

    const renderEnhancedContent = () => (
        <>
            {addedMetrics.length > 0 && (
                <div className={classes.addedSection}>
                    <div className={classes.sectionHeader}>
                        {t('ADDED METRICS')} ({addedMetrics.length})
                    </div>
                    {addedMetrics.map((metric) => (
                        <CollapsedMetricCard
                            key={metric.id}
                            metric={metric}
                            onExpand={handleExpandMetric}
                            onDelete={handleDeleteMetric}
                            onEdit={handleEditMetric}
                            onSaveEdit={handleSaveEdit}
                            onCancelEdit={handleCancelEdit}
                            availableFields={getAvailableFieldsForMetric(metric.id)}
                            calculationFunctions={calculationFunctions}
                            existingMetricNames={existingMetricNames.filter((n) => n !== metric.prettyName)}
                            selectedReportType={selectedReportType}
                        />
                    ))}
                </div>
            )}

            {draftMetric && (
                <div className={classes.draftSection}>
                    <div className={classes.sectionHeader}>{t('NEW METRIC')}</div>
                    <DraftMetricCard
                        data={draftMetric}
                        onChange={handleUpdateDraft}
                        onAdd={handleAddMetric}
                        onCancel={handleCancelDraft}
                        availableFields={availableFields}
                        calculationFunctions={calculationFunctions}
                        existingMetricNames={existingMetricNames}
                        customColumnType={customColumnType as 'global' | 'local'}
                        selectedReportType={selectedReportType}
                    />
                </div>
            )}

            {!draftMetric && addedMetrics.length > 0 && (
                <Button
                    type="link"
                    onClick={handleAddAnotherMetric}
                    style={{ padding: 0 }}
                >
                    + {t('Add Another Metric')}
                </Button>
            )}
        </>
    );

    const renderLegacyContent = () => (
        <>
            {columns.filter(data => data != null).map((data, j) => {
                return <CustomColumnsDataCard
                    key={data.id || j}
                    onRemove={() => {onRemove(data); setToRemove(true);}}
                    onChange={(data) => {
                        onChange(data.id, data);
                    }}
                    data={data} 
                    selectedReportType={selectedReportType}
                    customColumnType={customColumnType}
                    />;
            })}
            <Divider/>
            {!hideAddMoreOption &&
                <Button onClick={addMoreColumn} style={{padding:'0'}} type="link">+ {t('Add More')}</Button>
            }
        </>
    );

    return <Drawer
        placement="right"
        width="50%"
        bodyStyle={{padding: '0px'}}
        closable={false}
        visible={visible}>
            <Affix offsetTop={1} className={classes.affixStyle}>
                <DrawerTitleBar
                    onClickButton={addCustomFields}
                    onClose={() => setCustomColumnType('')}
                    title={t('Custom Metric')}
                    buttonText={t('Done')}/>
            </Affix>
            <div className={classes.body}>
                <div className={classes.descriptionText}>{t('Add multiple customized fields')}</div>
                {useEnhancedCustomMetrics ? renderEnhancedContent() : renderLegacyContent()}
            </div>
    </Drawer>;
};

export default CustomColumnDrawer;
